import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  Observable,
  createHttpLink,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { ChakraProvider, createSystem, defaultConfig } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import axios from "axios";
import React from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "react-hot-toast";
import "react-lazy-load-image-component/src/effects/blur.css";
import { BrowserRouter as Router } from "react-router-dom";
import "react-widgets/styles.css";
import { typeDefs } from "src/utils/graphql/typeDefs";
import "../src/styles/style.scss";
import App from "./App";
import { updateStorageStatus } from "./graphql/state/auth/authActions";
import { accessToken } from "./graphql/state/auth/authState";
import { networkError as netError } from "./graphql/state/global/networkState";
import {
  getAccessToken,
  getRefreshToken,
  removeAllFromStorage,
  saveToStorage,
} from "./helper/storage";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [new BrowserTracing()],
    environment: process.env.SENTRY_ENV || "development",
    tracesSampleRate: 1.0,
  });
}

export const httpLink = createHttpLink({
  uri: process.env.REACT_APP_API,
});

const authLink = new ApolloLink((operation, forward) => {
  // Retrieve the authorization token from the inMemory cache
  const token = getAccessToken();

  // Use the setContext method to set the HTTP headers.
  operation.setContext({
    headers: {
      "x-user-token": token ? `${token}` : "",
    },
  });

  // Call the next link in the middleware chain.
  return forward(operation);
});

const refreshTokens = async (): Promise<string> => {
  const refreshToken = getRefreshToken() as string;

  if (!refreshToken) {
    return "";
  }

  try {
    const result = await axios({
      url: process.env.REACT_APP_API,
      method: "post",
      data: {
        query: `
          query refresh($refresh_token: String!) {
            refresh(refresh_token: $refresh_token) {
              refresh_token
              access_token
              access_expiry
            }
          }
        `,
        variables: {
          refresh_token: refreshToken,
        },
      },
    });

    if (result?.data?.data?.refresh?.access_token) {
      const { refresh_token, access_token, access_expiry } =
        result?.data?.data?.refresh;
      const jwt = { refresh_token, access_token, access_expiry };

      saveToStorage("inawo", jwt);
      return result.data.data.refresh.access_token;
    }
    return "";
  } catch (error: any) {
    // Don't redirect if already on /clear or /login to prevent loops
    if (
      window.location.pathname === "/clear" ||
      window.location.pathname === "/login"
    ) {
      return "";
    }

    if (
      error?.response?.data?.errors?.[0]?.message === "unauthenticated" ||
      error?.message === "unauthenticated"
    ) {
      updateStorageStatus(false);
      removeAllFromStorage();
      accessToken("");
      window.location.replace("/login");
    }
    return "";
  }
};

const errorLink = onError(
  ({ networkError, graphQLErrors, operation, forward }) => {
    if (graphQLErrors) {
      for (let error of graphQLErrors) {
        if (error.message === "unauthenticated") {
          // Don't try to refresh if already on /clear or /login to prevent loops
          if (
            window.location.pathname === "/clear" ||
            window.location.pathname === "/login"
          ) {
            return new Observable(() => {
              throw error;
            });
          }

          // Refresh token and retry the operation
          return new Observable((observer) => {
            let subscriber: any;

            refreshTokens()
              .then((newToken) => {
                if (newToken) {
                  const oldHeaders = operation.getContext().headers;
                  operation.setContext({
                    headers: {
                      ...oldHeaders,
                      "x-user-token": newToken,
                    },
                  });
                  // Retry the operation with the new token
                  subscriber = forward(operation).subscribe({
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer),
                  });
                } else {
                  // Token refresh failed, throw the original error
                  observer.error(error);
                }
              })
              .catch((refreshError) => {
                observer.error(refreshError);
              });

            // Return cleanup function
            return () => {
              if (subscriber) subscriber.unsubscribe();
            };
          });
        } else if (error.extensions) {
          switch ((error.extensions as any).http?.status) {
            // throw error if too many requests
            case 429:
              return new Observable(() => {
                throw new Error("Too many requests");
              });

            case 404:
              return new Observable(() => {
                throw new Error("404: Not found");
              });
          }
        }
      }
    }
    if (networkError) {
      if (networkError.message === "Failed to fetch") netError(true);
    }
  }
);

export const client = new ApolloClient({
  link: errorLink.concat(authLink.concat(httpLink)),
  typeDefs,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          events: {
            // Treat isInvited as a key (different lists), but page/pageSize are for pagination
            keyArgs: ["isInvited"],
            merge(existing = [], incoming, { args }) {
              if (!args || args.page == null || args.pageSize == null) {
                // Fallback: replace if no pagination info
                return incoming;
              }
              const start = (args.page - 1) * args.pageSize;
              const merged = existing ? existing.slice(0) : [];
              for (let i = 0; i < incoming.length; i += 1) {
                merged[start + i] = incoming[i];
              }
              return merged;
            },
          },
        },
      },
      Wallet: {
        fields: {
          id: {
            merge: false,
          },
        },
      },
    },
  }),
});

const system = createSystem(defaultConfig);

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <ChakraProvider value={system}>
      <ApolloProvider client={client}>
        <Router>
          <Toaster
            position="top-center"
            reverseOrder={false}
            containerStyle={{
              zIndex: "2147483647",
              isolation: "isolate",
              transformStyle: "preserve-3d",
              transform: "translateZ(1000px)",
            }}
            toastOptions={{
              duration: 3000,
              success: {
                style: {
                  background: "#E9FBED",
                  border: "0.5px dashed #1FAD3E",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px 8px",
                  fontFamily: "Inter",
                  fontStyle: "normal",
                  fontWeight: "400",
                  fontSize: "14px",
                  lineHeight: "19px",
                  textAlign: "center",
                  color: "#34544F",
                  borderRadius: "0px",
                  zIndex: "2147483647",
                  isolation: "isolate",
                  transformStyle: "preserve-3d",
                  transform: "trasnslateZ(1000px)",
                },
              },
              error: {
                style: {
                  background: "#FCE8E8",
                  border: "0.5px dashed #B81414",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px 8px",
                  fontFamily: "Inter",
                  fontStyle: "normal",
                  fontWeight: "400",
                  fontSize: "14px",
                  lineHeight: "19px",
                  textAlign: "center",
                  color: "#8A0F0F",
                  borderRadius: "0px",
                  zIndex: "2147483647",
                  isolation: "isolate",
                  transformStyle: "preserve-3d",
                  transform: "trasnslateZ(1000px)",
                },
              },
              loading: {
                style: {
                  background: "#E9FBED",
                  border: "0.5px dashed #1FAD3E",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px 8px",
                  fontFamily: "Inter",
                  fontStyle: "normal",
                  fontWeight: "400",
                  fontSize: "14px",
                  lineHeight: "19px",
                  textAlign: "center",
                  color: "#34544F",
                  borderRadius: "0px",
                  zIndex: "2147483647",
                  isolation: "isolate",
                  transformStyle: "preserve-3d",
                  transform: "trasnslateZ(1000px)",
                },
              },
            }}
          />
          <App />
        </Router>
      </ApolloProvider>
    </ChakraProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// PWA support has been removed. Clear service workers installed by older releases.
serviceWorkerRegistration.unregister();
